import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Mail, Loader2 } from 'lucide-react';

const API_URL = window.location.origin;

const CoachSettingsTab = () => {
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/account/password`,
        {
          current_password: passwordForm.current,
          new_password: passwordForm.new
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully');
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      console.error('Password change failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="coach-settings-tab">
      <h2 className="font-heading text-xl font-semibold text-forest mb-4">Account Settings</h2>
      
      {/* Change Password */}
      <Card className="card-base mb-6">
        <CardHeader>
          <CardTitle className="text-forest flex items-center gap-2">
            <Lock className="h-5 w-5" /> Change Password
          </CardTitle>
          <CardDescription>Update your account password for security</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={passwordForm.current}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                placeholder="Enter your current password"
                required
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordForm.new}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new: e.target.value }))}
                placeholder="Enter new password (min 8 characters)"
                minLength={8}
                required
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                placeholder="Confirm new password"
                required
                data-testid="confirm-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="bg-forest hover:bg-forest/90"
              disabled={changingPassword}
              data-testid="change-password-btn"
            >
              {changingPassword ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Changing...</>
              ) : (
                'Change Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-forest flex items-center gap-2">
            <Mail className="h-5 w-5" /> Need Help?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            If you have any questions about your account, profile, or the coach portal, please contact us.
          </p>
          <a 
            href="mailto:info@volitionenterprises.com" 
            className="inline-flex items-center gap-2 text-forest hover:underline font-medium"
            data-testid="support-email-link"
          >
            <Mail className="h-4 w-4" /> info@volitionenterprises.com
          </a>
        </CardContent>
      </Card>
    </div>
  );
};

export default CoachSettingsTab;
